import { useCallback, useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import styled from "styled-components";

import { Info24px } from "@similarweb/icons";
import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { EmptyState, IconButton, Pagination, SegmentedControl, Tooltip } from "@similarweb/ui-components";
import type { SegmentedControlValue } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { getCountries } from "app/components/filters-bar/utils";
import { createKeywordGroupEntity, getRecentKeywordListEntities } from "app/modules/demand-analysis/keyword-lists";
import type { ICountryObject } from "app/services/CountryService";
import { type IUserKeywordGroup, useKeywordGroups, useSharedKeywordGroups } from "app/services/keyword-groups";
import { keywordsGroupService } from "app/services/keywordsGroup/keywordsGroupService";
import { RecentService } from "app/services/recent/recentService";
import { allTrackers } from "app/services/track/track";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE } from "pro-features/pages/module start page/src/keyword analysis/consts";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRecentObject } from "app/@types/userdata";

import { resolveAllowedCountry } from "../../shared/countryResolution";
import { KeywordListComposer } from "../keyword-list-composer/KeywordListComposer";
import { trackPopupOpen } from "../keyword-list-composer/tracking";

import { TrackSectionEntityCard } from "./TrackSectionEntityCard";
import { TrackSectionEntityCardSkeleton } from "./TrackSectionEntityCardSkeleton";
import { TrackSectionSortDropdown, type SortOptionId } from "./TrackSectionSortDropdown";
import { useKeywordListsTrendsData } from "./useKeywordListsTrendsData";

const PAGE_SIZE = 10;

const EmptyStateWrapper = styled(EmptyState)`
    margin: 24px 0;

    /* TODO: fix empty state component in ui-components so that it doesn't have unwanted margins
    https://similarweb-rnd.atlassian.net/browse/PFG-260 */
    div:last-child {
        margin-bottom: 0;
    }
`;

const skeletonCards = Array.from({ length: 4 }).map((_, index) => (
    <TrackSectionEntityCardSkeleton key={`entity-card-skeleton-${index}`} />
));

const TrackSectionSkeletonCards = () => skeletonCards;

export interface TrackSectionProps {
    countryId: number;
    callToActionButtonMessage: string;
    selectedTabIndex: number;
    onTabChange: (tabIndex: number) => void;
    onEntityEditClick: (item: IUserKeywordGroup) => void;
    onEntityShareClick: (item: IUserKeywordGroup) => void;
    onEntityDeleteClick: (item: IUserKeywordGroup) => void;
}

export const TrackSection = ({
    countryId,
    callToActionButtonMessage,
    selectedTabIndex,
    onTabChange,
    onEntityEditClick,
    onEntityShareClick,
    onEntityDeleteClick,
}: TrackSectionProps) => {
    const t = useTranslation();
    const [isNewKeywordListModalOpen, setIsNewKeywordListModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedSortId, setSelectedSortId] = useState<SortOptionId>("recently_viewed");
    const isOwnEntities = selectedTabIndex === 0;
    const recents = RecentService.getRecents() as IRecentObject[];
    const worldwide: ICountryObject | undefined = getCountries()?.at(0);

    const recentKeywordIndexMap = useMemo(() => {
        const map: Map<string, { index: number }> = new Map();
        const recentEntities = getRecentKeywordListEntities(recents);

        recentEntities.forEach((recentEntity, index) => {
            if (recentEntity.entity.type !== "keywordGroup") {
                return;
            }

            // The searches list might contain duplicate entries for the same keyword
            // We only want to store the most recent entry (lowest index = most recent)
            const existingEntry = map.get(recentEntity.entity.id);
            if (!existingEntry || index < existingEntry.index) {
                map.set(recentEntity.entity.id, { index });
            }
        });

        return map;
    }, [recents]);

    const keywordGroupsCompareFn = useCallback(
        (left: IUserKeywordGroup, right: IUserKeywordGroup) => {
            const leftItemInfo = recentKeywordIndexMap.get(left.Id);
            const rightItemInfo = recentKeywordIndexMap.get(right.Id);

            // Items with recent view info should always come before items without recent view info
            if (leftItemInfo && !rightItemInfo) {
                return -1; // left comes first
            }
            if (!leftItemInfo && rightItemInfo) {
                return 1; // right comes first
            }

            if (!leftItemInfo && !rightItemInfo) {
                return 0;
            }

            // If both have recent view info, sort by index (lower index = more recent)
            if (leftItemInfo && rightItemInfo) {
                return leftItemInfo.index - rightItemInfo.index;
            }

            // If neither has recent view info, fall back to LastUpdated date
            const dateLeft = dayjs(left.LastUpdated);
            const dateRight = dayjs(right.LastUpdated);

            if (dateLeft.isSame(dateRight)) {
                return 0;
            }

            return dateLeft.isBefore(dateRight) ? 1 : -1;
        },
        [recentKeywordIndexMap],
    );
    const select = useCallback(
        (data: IUserKeywordGroup[]) => data.toSorted(keywordGroupsCompareFn),
        [keywordGroupsCompareFn],
    );
    const canShareKeywordsGroup = keywordsGroupService.canShareKeywordsGroup();
    const { isLoading: isUserGroupsLoading, data: userGroups = [] } = useKeywordGroups({ select });
    const { isLoading: isSharedGroupsLoading, data: sharedGroups = [] } = useSharedKeywordGroups({ select });
    const isLoading = isOwnEntities ? isUserGroupsLoading : isSharedGroupsLoading;
    const displayItems = isOwnEntities ? userGroups : sharedGroups;

    const segmentedControlValues = useMemo<SegmentedControlValue[]>(() => {
        const myListsTab: SegmentedControlValue = {
            label: t("demand_analysis.home.library.my_lists_tab.title"),
            icon: "nav-keyword-group",
            counter: isUserGroupsLoading ? undefined : userGroups.length,
        };

        if (!canShareKeywordsGroup) {
            return [myListsTab];
        }

        const sharedListsTab: SegmentedControlValue = {
            label: t("demand_analysis.home.library.shared_lists_tab.title"),
            icon: "users",
            counter: isSharedGroupsLoading ? undefined : sharedGroups.length,
        };

        return [myListsTab, sharedListsTab];
    }, [t, userGroups.length, sharedGroups.length, canShareKeywordsGroup, isUserGroupsLoading, isSharedGroupsLoading]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedTabIndex]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedSortId]);

    const totalPages = Math.ceil(displayItems.length / PAGE_SIZE);

    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const sortedItems = useMemo(() => {
        // When "recently_viewed" is selected, displayItems is already sorted
        // by keywordGroupsCompareFn via the select function in React Query hooks
        if (selectedSortId === "recently_viewed") {
            return displayItems;
        }

        return displayItems.toSorted((left, right) => {
            const leftDate = dayjs(left.AddedTime ?? left.LastUpdated);
            const rightDate = dayjs(right.AddedTime ?? right.LastUpdated);

            if (!leftDate.isValid() && !rightDate.isValid()) {
                return 0;
            }
            if (!leftDate.isValid()) {
                return 1;
            }
            if (!rightDate.isValid()) {
                return -1;
            }

            if (leftDate.isSame(rightDate)) {
                return 0;
            }

            return leftDate.isBefore(rightDate) ? 1 : -1;
        });
    }, [displayItems, selectedSortId]);

    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const paginatedItems = sortedItems.slice(startIndex, startIndex + PAGE_SIZE);
    const showPagination = sortedItems.length > PAGE_SIZE;

    const { data: trendDataMap, isLoading: isTrendDataLoading } = useKeywordListsTrendsData(paginatedItems, countryId);

    const handleEntityClick = (item: IUserKeywordGroup) => {
        allTrackers.dispatchTrackEvent("Select keyword list", "click", "List", {
            target_KW_list_type: "Keyword list",
            target_KW_list_name: item.Name,
            target_KW_list_ID: item.Id,
            target_KW_list_shared_with_me: String(!isOwnEntities),
            target_KW_list_num_keywords: item.Keywords.length,
        });

        try {
            const state = proNavigator.getState(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE);
            const resolvedCountry = resolveAllowedCountry(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE, countryId);

            const keywordListEntity = createKeywordGroupEntity(
                { type: "KeywordGroup", keywordGroupId: item.Id },
                item.Name,
            );

            void proNavigator.go(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE, {
                ...state.defaultQueryParams,
                id: [keywordListEntity.keywordListId],
                country: resolvedCountry,
            });
        } catch (error) {
            Sentry.error("[Track Section] Failed to navigate to keyword group", error as Error, {
                tags: { team: "ants" },
            });
        }
    };

    const handleNewKeywordGroupClick = () => {
        setIsNewKeywordListModalOpen(true);
        allTrackers.dispatchTrackEvent("Button", "click", "new keyword list");
        trackPopupOpen("create");
    };

    return (
        <>
            <Flex
                data-automation="demand-analysis-homepage-library-widget"
                flexDirection="column"
                gap="16px"
                alignItems="stretch"
            >
                <Flex
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Flex
                        alignItems="center"
                        gap="4px"
                    >
                        <Text
                            as="h5"
                            fontVariant="h5"
                            color={colorsPalettes.carbon[500]}
                        >
                            {t("demand_analysis.home.library.title.text")}
                        </Text>
                        <Tooltip content={t("demand_analysis.home.library.title.tooltip")}>
                            <Info24px
                                size="sm"
                                color={colorsPalettes.carbon[200]}
                            />
                        </Tooltip>
                    </Flex>
                    <IconButton
                        type="outlined"
                        iconName="add"
                        onClick={handleNewKeywordGroupClick}
                    >
                        {t(callToActionButtonMessage)}
                    </IconButton>
                </Flex>
                <Flex
                    flexDirection="column"
                    gap="8px"
                    padding="8px"
                    borderRadius="6px"
                    alignItems="stretch"
                    background={colorsPalettes.sky[100]}
                >
                    <Flex
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <SegmentedControl
                            selectedIndex={selectedTabIndex}
                            values={segmentedControlValues}
                            onChange={onTabChange}
                        />
                        <TrackSectionSortDropdown
                            selectedSortId={selectedSortId}
                            onSortChange={setSelectedSortId}
                        />
                    </Flex>
                    {isLoading ? (
                        <TrackSectionSkeletonCards />
                    ) : paginatedItems.length === 0 ? (
                        <EmptyStateWrapper
                            iconName="no-data-tree"
                            title={t(
                                isOwnEntities
                                    ? "demand.analysis.home.library.empty_state.no_keyword_lists.heading"
                                    : "demand.analysis.home.library.empty_state.no_shared_keyword_lists.heading",
                            )}
                            subtitle={t("demand.analysis.home.library.empty_state.description")}
                        />
                    ) : (
                        paginatedItems.map((entity) => (
                            <TrackSectionEntityCard
                                key={entity.Id}
                                isEditable={isOwnEntities}
                                entity={entity}
                                isTrendLoading={isTrendDataLoading}
                                trendData={trendDataMap?.get(entity.Id)}
                                onEntityClick={handleEntityClick}
                                onEntityEditClick={onEntityEditClick}
                                onEntityShareClick={onEntityShareClick}
                                onEntityDeleteClick={onEntityDeleteClick}
                            />
                        ))
                    )}
                    {showPagination ? (
                        <Flex
                            justifyContent="flex-end"
                            paddingY="4px"
                        >
                            <Pagination
                                page={currentPage}
                                itemsCount={displayItems.length}
                                itemsPerPage={PAGE_SIZE}
                                handlePageChange={setCurrentPage}
                                captionPosition="center"
                                hasItemsPerPageSelect={false}
                            />
                        </Flex>
                    ) : null}
                </Flex>
            </Flex>
            {isNewKeywordListModalOpen ? (
                <KeywordListComposer
                    country={worldwide}
                    onClose={() => setIsNewKeywordListModalOpen(false)}
                />
            ) : null}
        </>
    );
};
